import AHBoolean from "common/values/boolean/boolean";
import Date from "common/values/date/date";
import Guid from "common/values/guid/guid";
import Percent from "common/values/percent/percent";
import _ from "lodash";
import Individual from "marketplace/entities/individual/individual";
import moment, { Moment } from "moment";
import User, { UserJSON } from "users/entities/user/user";
import Session from "users/session/session";
import EntityClientRepresentative, { EntityClientRepresentativeJSON } from "work/entities/entity-client-representative/entity-client-representative";
import EntityVendorRepresentative, { EntityVendorRepresentativeJSON } from "work/entities/entity-vendor-representative/entity-vendor-representative";
import Proposal, { CompleteProposalSpec, ProposalField, ProposalFieldCategory, ProposalFieldJSON, ProposalJSON, } from "work/entities/proposal/proposal";
import WorkAgreement, { Masked, MaskedContracts, MaskedDiscount, MaskedFeeSchedule, MaskedTeam } from "work/entities/work-agreement/work-agreement";
import { ProposalFieldName } from "work/values/constants";
import FeeScheduleCategory, {
  FeeScheduleCategoryJSON,
  FeeScheduleCategoryMaskedCurrency
} from "work/values/fee-schedule-category/fee-schedule-category";
import ProjectDescription from "work/values/project-description/project-description";
import ProjectName from "work/values/project-name/project-name";
import ProposalReviewer, { ProposalReviewerJSON } from "work/values/proposal-reviewer";
import DetailedVendorTeam, { DetailedVendorTeamJSON } from "work/values/vendor-team/detailed-vendor-team";
import WorkDocument, { WorkDocumentJSON } from "work/values/work-document/work-document";
import ProposalFactory from "./proposal-factory";

interface ProposalSpecJSON {
  client?: EntityClientRepresentativeJSON;
  vendors: EntityVendorRepresentativeJSON[];
  name?: string;
  description?: string;
  negotiable?: boolean;
  responseDueBy?: string;
  startDate?: string;
  endDate?: string;
  supersededBy?: ProposalJSON;
  supersedesId?: string;
  clientReviewers?: ProposalReviewerJSON[];
  vendorReviewers?: ProposalReviewerJSON[];
  discount?: number | typeof Masked.MaskedField;
  feeSchedule: FeeScheduleCategoryJSON[] | typeof Masked.MaskedField;
  conflictsDocuments: WorkDocumentJSON[] | typeof Masked.MaskedField;
  clientPolicyDocuments: WorkDocumentJSON[] | typeof Masked.MaskedField;
  vendorPolicyDocuments: WorkDocumentJSON[] | typeof Masked.MaskedField;
  team?: DetailedVendorTeamJSON | typeof Masked.MaskedField;
  clientTeamTemplateIds: string[];
  vendorTeamTemplateIds: string[];
  clientFeeScheduleTemplateIds: string[];
  vendorFeeScheduleTemplateIds: string[];
  teamRestricted?: boolean | typeof Masked.MaskedField;
  conflictsCheckWaived?: boolean | typeof Masked.MaskedField;
}

export class ProposalSpec {
  client?: EntityClientRepresentative;
  vendors: EntityVendorRepresentative[] = [];
  name?: ProjectName;
  description?: ProjectDescription;
  negotiable?: boolean;
  responseDueBy?: Date;
  startDate?: Date;
  endDate?: Date;
  supersededBy?: Proposal;
  supersedesId?: Guid;
  clientReviewers?: ProposalReviewer[];
  vendorReviewers?: ProposalReviewer[];
  discount: Percent | MaskedDiscount = new Percent(0.03);
  feeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule = [];
  conflictsDocuments: WorkDocument[] | MaskedContracts = [];
  clientPolicyDocuments: WorkDocument[] | MaskedContracts = [];
  vendorPolicyDocuments: WorkDocument[] | MaskedContracts = [];
  team?: DetailedVendorTeam | MaskedTeam;
  clientTeamTemplateIds: Guid[] = [];
  vendorTeamTemplateIds: Guid[] = [];
  clientFeeScheduleTemplateIds: Guid[] = [];
  vendorFeeScheduleTemplateIds: Guid[] = [];
  teamRestricted: AHBoolean | MaskedTeam = new AHBoolean(false);
  conflictsCheckWaived: AHBoolean | MaskedContracts = new AHBoolean(false);

  public clone(): ProposalSpec {
    const clone = new ProposalSpec();
    clone.client = this.client?.clone();
    clone.name = this.name?.clone();
    clone.description = this.description?.clone();
    clone.negotiable = this.negotiable;
    clone.responseDueBy = this.responseDueBy?.clone();
    clone.startDate = this.startDate?.clone();
    clone.endDate = this.endDate?.clone();
    clone.supersededBy = this.supersededBy;
    clone.supersedesId = this.supersedesId?.clone();
    clone.clientReviewers = this.clientReviewers?.concat();
    clone.vendorReviewers = this.vendorReviewers?.concat();
    clone.feeSchedule = Masked.isMasked(this.feeSchedule) ? this.feeSchedule :
      this.feeSchedule.map((category) => category.clone());
    clone.discount = Masked.isMasked(this.discount) ? this.discount : this.discount.clone();
    clone.conflictsDocuments = Masked.isMasked(this.conflictsDocuments) ? this.conflictsDocuments :
      this.conflictsDocuments.map((doc) => doc.clone());
    clone.clientPolicyDocuments = Masked.isMasked(this.clientPolicyDocuments) ? this.clientPolicyDocuments :
      this.clientPolicyDocuments.map((doc) => doc.clone());
    clone.vendorPolicyDocuments = Masked.isMasked(this.vendorPolicyDocuments) ? this.vendorPolicyDocuments :
      this.vendorPolicyDocuments.map((doc) => doc.clone());
    clone.team = Masked.isMasked(this.team) ? this.team : this.team?.clone();
    clone.clientTeamTemplateIds = this.clientTeamTemplateIds.map((id) =>
      id.clone()
    );
    clone.vendorTeamTemplateIds = this.vendorTeamTemplateIds.map((id) =>
      id.clone()
    );
    clone.clientFeeScheduleTemplateIds = this.clientFeeScheduleTemplateIds.map(
      (id) => id.clone()
    );
    clone.vendorFeeScheduleTemplateIds = this.vendorFeeScheduleTemplateIds.map(
      (id) => id.clone()
    );
    clone.teamRestricted = Masked.isMasked(this.teamRestricted) ?
      this.teamRestricted :
      this.teamRestricted.clone();
    clone.conflictsCheckWaived = Masked.isMasked(this.conflictsCheckWaived) ?
      this.conflictsCheckWaived :
      this.conflictsCheckWaived.clone();
    return clone;
  }

  // Helpers for isEqualTo
  private static isOptionalEqual<T extends { isEqualTo(other: T): boolean }>(
    a: T | null | undefined,
    b: T | null | undefined
  ): boolean {
    const aPresent = a != null;
    const bPresent = b != null;
    if (aPresent && bPresent) return a.isEqualTo(b);
    return aPresent === bPresent;
  }
  private static isUnorderedArrayEqual<T extends { isEqualTo(other: T): boolean }>(
    a: T[] | undefined,
    b: T[] | undefined
  ): boolean {
    if (!a || !b) return (!a && !b);
    if (a.length !== b.length) return false;
    return a.every(ax => b.some(bx => bx.isEqualTo(ax)))
      && b.every(bx => a.some(ax => ax.isEqualTo(bx)));
  }

  public isEqualTo(other: ProposalSpec): boolean {
    // Optionals (no masking)
    if (!ProposalSpec.isOptionalEqual(this.client, other.client)) return false;
    if (!ProposalSpec.isOptionalEqual(this.name, other.name)) return false;
    if (!ProposalSpec.isOptionalEqual(this.description, other.description)) return false;

    // Primitives
    if (this.negotiable !== other.negotiable) return false;

    // Dates (no masking)
    if (!ProposalSpec.isOptionalEqual(this.responseDueBy, other.responseDueBy)) return false;
    if (!ProposalSpec.isOptionalEqual(this.startDate, other.startDate)) return false;
    if (!ProposalSpec.isOptionalEqual(this.endDate, other.endDate)) return false;

    // Masked-aware single fields
    if (!Masked.isOptionalEqual(this.discount, other.discount)) return false;
    if (!Masked.isOptionalEqual(this.team, other.team)) return false;
    if (!Masked.isOptionalEqual(this.teamRestricted, other.teamRestricted)) return false;
    if (!Masked.isOptionalEqual(this.conflictsCheckWaived, other.conflictsCheckWaived)) return false;

    // Masked-aware arrays (unordered)
    if (!Masked.isUnorderedArrayEqual(this.feeSchedule, other.feeSchedule)) return false;
    if (!Masked.isUnorderedArrayEqual(this.conflictsDocuments, other.conflictsDocuments)) return false;
    if (!Masked.isUnorderedArrayEqual(this.clientPolicyDocuments, other.clientPolicyDocuments)) return false;
    if (!Masked.isUnorderedArrayEqual(this.vendorPolicyDocuments, other.vendorPolicyDocuments)) return false;

    // Plain arrays (unordered, no masking)
    if (!ProposalSpec.isUnorderedArrayEqual(this.clientTeamTemplateIds, other.clientTeamTemplateIds)) return false;
    if (!ProposalSpec.isUnorderedArrayEqual(this.vendorTeamTemplateIds, other.vendorTeamTemplateIds)) return false;
    if (!ProposalSpec.isUnorderedArrayEqual(this.clientFeeScheduleTemplateIds, other.clientFeeScheduleTemplateIds)) return false;
    if (!ProposalSpec.isUnorderedArrayEqual(this.vendorFeeScheduleTemplateIds, other.vendorFeeScheduleTemplateIds)) return false;

    // Optional reviewer arrays (unordered)
    if (!ProposalSpec.isUnorderedArrayEqual(this.clientReviewers, other.clientReviewers)) return false;
    return ProposalSpec.isUnorderedArrayEqual(this.vendorReviewers, other.vendorReviewers);


  }

  public toJSON(): ProposalSpecJSON {
    return {
      client: this.client?.toJSON(),
      vendors: this.vendors.map((vendor) => vendor.toJSON()),
      name: this.name?.toJSON(),
      description: this.description?.toJSON(),
      negotiable: this.negotiable,
      responseDueBy: this.responseDueBy?.toJSON(),
      startDate: this.startDate?.toJSON(),
      endDate: this.endDate?.toJSON(),
      supersededBy: this.supersededBy?.toJSON(),
      supersedesId: this.supersedesId?.toJSON(),
      clientReviewers: this.clientReviewers?.map((reviewer) => reviewer.toJSON()),
      vendorReviewers: this.vendorReviewers?.map((reviewer) => reviewer.toJSON()),
      discount: this.discount?.toJSON(),
      feeSchedule: Masked.isMasked(this.feeSchedule) ? Masked.MaskedField :
        this.feeSchedule.map((category) => category.toJSON()),
      conflictsDocuments: Masked.isMasked(this.conflictsDocuments) ? Masked.MaskedField :
        this.conflictsDocuments.map((doc) => doc.toJSON()),
      clientPolicyDocuments: Masked.isMasked(this.clientPolicyDocuments) ? Masked.MaskedField :
        this.clientPolicyDocuments.map((doc) => doc.toJSON()),
      vendorPolicyDocuments: Masked.isMasked(this.vendorPolicyDocuments) ? Masked.MaskedField :
        this.vendorPolicyDocuments.map((doc) => doc.toJSON()),
      team: Masked.isMasked(this.team) ? Masked.MaskedField :
        this.team?.toJSON(),
      clientTeamTemplateIds: this.clientTeamTemplateIds.map((id) => id.toJSON()),
      vendorTeamTemplateIds: this.vendorTeamTemplateIds.map((id) => id.toJSON()),
      clientFeeScheduleTemplateIds: this.clientFeeScheduleTemplateIds.map((id) => id.toJSON()),
      vendorFeeScheduleTemplateIds: this.vendorFeeScheduleTemplateIds.map((id) => id.toJSON()),
      teamRestricted: this.teamRestricted?.toJSON(),
      conflictsCheckWaived: this.conflictsCheckWaived?.toJSON()
    }
  }

  public static fromJSON(json: ProposalSpecJSON): ProposalSpec {
    const spec = new ProposalSpec();
    spec.client = EntityClientRepresentative.fromJSON(json.client);
    spec.vendors = json.vendors.map((vendor) =>
      EntityVendorRepresentative.fromJSON(vendor)
    );
    spec.name = json.name ? ProjectName.fromJSON(json.name) : undefined;
    spec.description = json.description ? ProjectDescription.fromJSON(json.description) : undefined;
    spec.negotiable = json.negotiable;
    spec.responseDueBy = Date.fromJSON(json.responseDueBy) ?? undefined;
    spec.startDate = Date.fromJSON(json.startDate) ?? undefined;
    spec.endDate = Date.fromJSON(json.endDate) ?? undefined;
    spec.supersedesId = json.supersedesId ? Guid.fromJSON(json.supersedesId) : undefined;
    spec.clientReviewers = json.clientReviewers?.map((reviewer) =>
      ProposalReviewer.fromJSON(reviewer)
    );
    spec.vendorReviewers = json.vendorReviewers?.map((reviewer) =>
      ProposalReviewer.fromJSON(reviewer)
    );
    spec.discount = Masked.isNotMasked(json.discount)
      ? Percent.fromJSON(json.discount)
      : new MaskedDiscount();
    spec.feeSchedule = Masked.isNotMasked(json.feeSchedule)
      ? json.feeSchedule.map((category) => FeeScheduleCategory.fromJSON(category))
      : new MaskedFeeSchedule();
    spec.conflictsDocuments = Masked.isNotMasked(json.conflictsDocuments)
      ? json.conflictsDocuments?.map((doc) => WorkDocument.fromJSON(doc))
      : new MaskedContracts();
    spec.clientPolicyDocuments = Masked.isNotMasked(json.clientPolicyDocuments)
      ? json.clientPolicyDocuments?.map((doc) => WorkDocument.fromJSON(doc))
      : new MaskedContracts();
    spec.vendorPolicyDocuments = Masked.isNotMasked(json.vendorPolicyDocuments)
      ? json.vendorPolicyDocuments?.map((doc) => WorkDocument.fromJSON(doc))
      : new MaskedContracts();
    spec.team = Masked.isPresentNotMasked(json.team)
      ? DetailedVendorTeam.fromJSON(json.team)
      : new MaskedTeam();
    spec.clientTeamTemplateIds = json.clientTeamTemplateIds?.map((id) =>
      Guid.fromJSON(id)
    );
    spec.vendorTeamTemplateIds = json.vendorTeamTemplateIds?.map((id) =>
      Guid.fromJSON(id)
    );
    spec.clientFeeScheduleTemplateIds = json.clientFeeScheduleTemplateIds?.map(
      (id) => Guid.fromJSON(id)
    );
    spec.vendorFeeScheduleTemplateIds = json.vendorFeeScheduleTemplateIds?.map(
      (id) => Guid.fromJSON(id)
    );
    spec.teamRestricted = Masked.isPresentNotMasked(json.teamRestricted)
      ? new AHBoolean(json.teamRestricted)
      : new MaskedTeam();
    spec.conflictsCheckWaived = Masked.isPresentNotMasked(json.conflictsCheckWaived)
      ? new AHBoolean(json.conflictsCheckWaived)
      : new MaskedContracts();

    return spec;
  }
}

interface ProposalSpecChangeJSON {
  field: ProposalFieldJSON | null;
  action: keyof typeof ProposalChangeAction;
  value: string;
}

export class ProposalSpecChange {
  field: ProposalField | null;
  action: ProposalChangeAction;
  value: string;
  timeStamp: Moment = moment();

  constructor(
    field: ProposalField,
    action: ProposalChangeAction,
    value: string
  ) {
    this.field = field ?? null;
    this.action = action ?? ProposalChangeAction.Edit;
    this.value = value ?? "";
  }

  public static fromJSON(json: ProposalSpecChangeJSON): ProposalSpecChange | undefined {
    if (!json.field) return;
    const field = ProposalField.fromJSON(json.field);
    const action = ProposalChangeAction.fromJSON(json.action);
    const value = json.value;
    return new ProposalSpecChange(
      field,
      action,
      value
    );
  }

  public toJSON(): ProposalSpecChangeJSON {
    return {
      field: this.field?.toJSON() ?? null,
      action: this.action.toString() as keyof typeof ProposalChangeAction,
      value: this.value,
    };
  }

  public clone(): ProposalSpecChange {
    const clone = ProposalSpecChange.fromJSON(this.toJSON());
    if (!clone) throw new Error("Failed to clone ProposalSpecChange");
    return clone;
  }
}

export class ProposalChangeAction {
  // The present tense of the action MUST match the readonly property name
  static readonly Edit: ProposalChangeAction = new ProposalChangeAction(
    "Edit",
    "Edited"
  );
  static readonly ReEdit: ProposalChangeAction = new ProposalChangeAction(
    "ReEdit",
    "Re-edited"
  );
  static readonly Add: ProposalChangeAction = new ProposalChangeAction(
    "Add",
    "Added"
  );
  static readonly Remove: ProposalChangeAction = new ProposalChangeAction(
    "Remove",
    "Removed"
  );

  private constructor(
    private readonly presentTense: string,
    public readonly actionDescription: string
  ) {
  }

  public toString() {
    return this.presentTense;
  }

  public static fromJSON(
    obj: keyof typeof ProposalChangeAction
  ): ProposalChangeAction {
    return ProposalChangeAction[obj] as ProposalChangeAction;
  }
}

interface ProposalBuilderJSON {
  specStack: ProposalSpecJSON[];
  currentIndex: number;
  lastSavedIndex?: number;
  sessionHistory: ProposalSpecChangeJSON[];
  lastUpdated: string;
  currentUser: UserJSON;
}

export default class ProposalBuilder {
  protected _specStack: ProposalSpec[] = [new ProposalSpec()];
  private _currentIndex: number = 0;
  private _lastSavedIndex: number | undefined = undefined;
  private _sessionHistory: ProposalSpecChange[] = [];
  protected _lastUpdated: Date;
  private readonly _currentUser: User;

  constructor(currentUser: User | null, startingSpec?: CompleteProposalSpec, lastUpdated?: Date) {
    if (!currentUser) throw new Error("currentUser is required");
    this._currentUser = currentUser;
    if (startingSpec) {
      const spec = new ProposalSpec();
      spec.client = startingSpec.client;
      spec.name = startingSpec.name;
      spec.description = startingSpec.description;
      spec.negotiable = startingSpec.negotiable;
      spec.responseDueBy = startingSpec.responseDueBy;
      spec.startDate = startingSpec.startDate;
      spec.endDate = startingSpec.endDate;
      spec.clientReviewers = startingSpec.clientReviewers;
      spec.vendorReviewers = startingSpec.vendorReviewers;
      spec.discount = startingSpec.discount;
      spec.feeSchedule = startingSpec.feeSchedule;
      spec.conflictsDocuments = startingSpec.conflictsDocuments;
      spec.clientPolicyDocuments = startingSpec.clientPolicyDocuments;
      spec.vendorPolicyDocuments = startingSpec.vendorPolicyDocuments;
      spec.team = startingSpec.vendorTeam;
      spec.clientTeamTemplateIds = startingSpec.clientTeamTemplateIds;
      spec.vendorTeamTemplateIds = startingSpec.vendorTeamTemplateIds;
      spec.clientFeeScheduleTemplateIds =
        startingSpec.clientFeeScheduleTemplateIds;
      spec.vendorFeeScheduleTemplateIds =
        startingSpec.vendorFeeScheduleTemplateIds;
      spec.teamRestricted = startingSpec.teamRestricted;
      spec.conflictsCheckWaived = startingSpec.conflictsCheckWaived;
      this._specStack = [spec];
      this._currentIndex = 0;
    }
    this._lastUpdated = lastUpdated ?? new Date();
  }

  public get currentSpec(): ProposalSpec {
    if (this._currentIndex >= this._specStack.length || this._currentIndex < 0)
      throw new Error("Invalid index");
    return this._specStack[this._currentIndex];
  }

  public get sessionHistory(): ProposalSpecChange[] {
    return this._sessionHistory;
  }

  public get isModified(): boolean {
    if (this._currentIndex === 0) return false;
    return !this.currentSpec.isEqualTo(this._specStack[this._lastSavedIndex ?? 0]);
  }

  public get lastUpdated(): Date {
    return this._lastUpdated;
  }

  public cleanStack(): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder._specStack = [newBuilder.currentSpec.clone()];
    newBuilder._currentIndex = 0;
    newBuilder._lastSavedIndex = 0;
    return newBuilder;
  }

  public setLastSavedIndex(lastSavedIndex?: number): void {
    if (lastSavedIndex !== undefined) {
      this._lastSavedIndex = lastSavedIndex;
    } else {
      this._lastSavedIndex = this._currentIndex;
    }
  }

  public resetSession(): ProposalBuilder {
    const newBuilder = this.clone();
    if (!this._lastSavedIndex) {
      return newBuilder;
    }
    newBuilder._specStack = newBuilder._specStack.slice(
      0,
      this._lastSavedIndex + 1
    );
    newBuilder._currentIndex = newBuilder._lastSavedIndex ?? 0;
    return newBuilder;
  }

  public setClient(client?: EntityClientRepresentative): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();

    const newSpec = this.currentSpec.clone();
    newSpec.client = client;

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public setName(name: ProjectName): ProposalBuilder {
    if (this.currentSpec.name?.value === name.value) {
      return this;
    }
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.name = name;
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.Name)
    );
    if (
      (!newBuilder._specStack[0].name?.value && !name.value) ||
      newBuilder._specStack[0].name?.isEqualTo(name)
    ) {
      return newBuilder;
    }
    let action: ProposalChangeAction = ProposalChangeAction.Edit;
    let value = newBuilder._specStack[0].name?.value ?? "";
    if (!newBuilder._specStack[0].name && name) {
      action = ProposalChangeAction.Add;
      value = name.value;
    } else if (newBuilder._specStack[0].name?.value && !name.value) {
      action = ProposalChangeAction.Remove;
    }
    const specChange = new ProposalSpecChange(
      ProposalField.Name,
      action,
      value
    );
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setDescription(description: ProjectDescription): ProposalBuilder {
    if (
      (!description && !this.currentSpec.description) ||
      this.currentSpec.description?.isEqualTo(description)
    ) {
      return this;
    }
    const newBuilder = this.clone();

    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.description = description;
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.Description)
    );

    if (
      (!newBuilder._specStack[0].description?.value && !description?.value) ||
      newBuilder._specStack[0].description?.isEqualTo(description)
    ) {
      return newBuilder;
    }
    let action: ProposalChangeAction = ProposalChangeAction.Edit;
    let value = newBuilder._specStack[0].description?.value ?? "";
    if (!newBuilder._specStack[0].description?.value && description?.value) {
      action = ProposalChangeAction.Add;
      value = description.value;
    } else if (
      newBuilder._specStack[0].description?.value &&
      !description?.value
    ) {
      action = ProposalChangeAction.Remove;
    }
    const specChange = new ProposalSpecChange(
      ProposalField.Description,
      action,
      value
    );
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setNegotiable(negotiable: boolean): ProposalBuilder {
    if (this.currentSpec.negotiable === negotiable) {
      return this;
    }
    const newBuilder = this.clone();

    this._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.Negotiable)
    );

    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.negotiable = negotiable;
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    if (newBuilder._specStack[0].negotiable === negotiable) {
      return newBuilder;
    }
    const action = ProposalChangeAction.Edit;
    const specChange = new ProposalSpecChange(
      ProposalField.Negotiable,
      action,
      newBuilder._specStack[0].negotiable ? "Negotiable" : "Not Negotiable"
    );
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setResponseDueBy(responseDueBy: Date | undefined): ProposalBuilder {
    if (
      (!this.currentSpec.responseDueBy && !responseDueBy) ||
      this.currentSpec.responseDueBy?.isEqualTo(responseDueBy)
    ) {
      return this;
    }

    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.responseDueBy = responseDueBy;
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.ResponseDueBy)
    );

    if (
      (!newBuilder._specStack[0].responseDueBy && !responseDueBy) ||
      newBuilder._specStack[0].responseDueBy?.isEqualTo(responseDueBy)
    ) {
      return newBuilder;
    }
    let action: ProposalChangeAction = ProposalChangeAction.Edit;
    let value =
      newBuilder._specStack[0].responseDueBy?.format("MM/DD/YY") ?? "";
    if (!newBuilder._specStack[0].responseDueBy && responseDueBy) {
      action = ProposalChangeAction.Add;
      value = responseDueBy.format("MM/DD/YY");
    } else if (newBuilder._specStack[0].responseDueBy && !responseDueBy) {
      action = ProposalChangeAction.Remove;
    }
    const specChange = new ProposalSpecChange(
      ProposalField.ResponseDueBy,
      action,
      value
    );
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setStartDate(startDate: Date | undefined): ProposalBuilder {
    if (
      (!this.currentSpec.startDate && !startDate) ||
      this.currentSpec.startDate?.isEqualTo(startDate)
    ) {
      return this;
    }

    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.startDate = startDate;
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.StartDate)
    );
    if (
      (!newBuilder._specStack[0].startDate && !startDate) ||
      newBuilder._specStack[0].startDate?.isEqualTo(startDate)
    ) {
      return newBuilder;
    }
    let action: ProposalChangeAction = ProposalChangeAction.Edit;
    let value = newBuilder._specStack[0].startDate?.format("MM/DD/YY") ?? "";
    if (!newBuilder._specStack[0].startDate && startDate) {
      action = ProposalChangeAction.Add;
      value = startDate.format("MM/DD/YY");
    } else if (newBuilder._specStack[0].startDate && !startDate) {
      action = ProposalChangeAction.Remove;
    }
    const specChange = new ProposalSpecChange(
      ProposalField.StartDate,
      action,
      value
    );
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setEndDate(endDate: Date | undefined): ProposalBuilder {
    if (
      (!this.currentSpec.endDate && !endDate) ||
      this.currentSpec.endDate?.isEqualTo(endDate)
    ) {
      return this;
    }

    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.endDate = endDate;
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.EndDate)
    );

    if (
      (!newBuilder._specStack[0].endDate && !endDate) ||
      newBuilder._specStack[0].endDate?.isEqualTo(endDate)
    ) {
      return newBuilder;
    }
    let action: ProposalChangeAction = ProposalChangeAction.Edit;
    let value = newBuilder._specStack[0].endDate?.format("MM/DD/YY") ?? "";
    if (!newBuilder._specStack[0].endDate && endDate) {
      action = ProposalChangeAction.Add;
      value = endDate.format("MM/DD/YY");
    } else if (newBuilder._specStack[0].endDate && !endDate) {
      action = ProposalChangeAction.Remove;
    }
    const specChange = new ProposalSpecChange(
      ProposalField.EndDate,
      action,
      value
    );
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setTeam(newTeam: DetailedVendorTeam | undefined): ProposalBuilder {
    if (
      Masked.isMasked(this.currentSpec.team) ||
      (!newTeam && !this.currentSpec.team) ||
      this.currentSpec.team?.isEqualTo(newTeam) ||
      Masked.isMasked(this._specStack[0].team)
    ) {
      return this;
    }

    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.team = newTeam;

    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => change.field?.category !== ProposalFieldCategory.Team
    );

    if (Masked.isMasked(newSpec.teamRestricted)) {
      throw new Error("Cannot compare team restriction changes when new team restriction is masked");
    }
    if (Masked.isMasked(newBuilder._specStack[0].team)) {
      throw new Error("Cannot compare team changes when original team is masked");
    }
    if (Masked.isMasked(newBuilder._specStack[0].teamRestricted)) {
      throw new Error("Cannot compare team restriction changes when original team restriction is masked");
    }
    if (Masked.isMasked(newBuilder._specStack[0].discount)) {
      throw new Error("Cannot compare discount changes when original discount is masked");
    }

    const addedLeader =
      newTeam?.leader?.userId.value &&
      !newBuilder._specStack[0].team?.leader?.userId.value;

    const removedLeader =
      !newTeam?.leader?.userId.value &&
      newBuilder._specStack[0].team?.leader?.userId.value;

    if (
      addedLeader &&
      newTeam.leader &&
      !newTeam.leader?.userId.isEqualTo(newBuilder._specStack[0].team?.leader?.userId)
    ) {
      const specChange = new ProposalSpecChange(
        ProposalField.TeamLeader,
        ProposalChangeAction.Add,
        newTeam?.leader?.getFullName()
      );
      newBuilder._sessionHistory.push(specChange);
    }

    if (removedLeader && newBuilder._specStack[0].team?.leader) {
      const specChange = new ProposalSpecChange(
        ProposalField.TeamLeader,
        ProposalChangeAction.Remove,
        newBuilder._specStack[0].team?.leader.getFullName()
      );
      newBuilder._sessionHistory.push(specChange);
    }

    for (const newMember of newTeam?.members ?? []) {
      const existingMember = newBuilder._specStack[0].team?.members.some(
        (originalMember) => originalMember.userId.isEqualTo(newMember.userId)
      );
      if (!existingMember) {
        const specChange = new ProposalSpecChange(
          ProposalField.TeamMember(newMember.userId),
          ProposalChangeAction.Add,
          newMember.getFullName()
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }
    for (const originalMember of newBuilder._specStack[0].team?.members ?? []) {
      const removedMember = !newTeam?.members.some((newMember) =>
        newMember.userId.isEqualTo(originalMember.userId)
      );

      if (removedMember) {
        const specChange = new ProposalSpecChange(
          ProposalField.TeamMember(originalMember.userId),
          ProposalChangeAction.Remove,
          originalMember.getFullName()
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }

    if (
      newBuilder._specStack[0]?.teamRestricted?.value !==
      newSpec.teamRestricted?.value
    ) {
      const specChange = new ProposalSpecChange(
        ProposalField.TeamRestriction,
        newSpec.teamRestricted?.value
          ? ProposalChangeAction.Add
          : ProposalChangeAction.Remove,
        "Team Restriction"
      );
      newBuilder._sessionHistory.push(specChange);
    }
    return newBuilder;
  }

  public setFeeSchedule(
    categories: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | undefined
  ): ProposalBuilder {
    const newBuilder = this.clone();
    if (Masked.isMasked(newBuilder._specStack[0].feeSchedule)) {
      throw new Error("Cannot compare fee schedule changes when original fee schedule is masked");
    }

    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.feeSchedule = categories ?? [];
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => change.field?.category !== ProposalFieldCategory.FeeSchedule
    );
    for (const category of categories ?? []) {
      const existingCategory = newBuilder._specStack[0].feeSchedule?.find(
        (cat) => cat.id.isEqualTo(category.id)
      );
      if (!existingCategory) {
        const specChange = new ProposalSpecChange(
          ProposalField.FeeScheduleCategory(category.id),
          ProposalChangeAction.Add,
          category.toString()
        );
        newBuilder._sessionHistory.push(specChange);
      } else if (!category.isEqualTo(existingCategory)) {
        const specChange = new ProposalSpecChange(
          ProposalField.FeeScheduleCategory(category.id),
          ProposalChangeAction.Edit,
          existingCategory.toString()
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }
    for (const category of newBuilder._specStack[0].feeSchedule ?? []) {
      const removedCategory = !categories?.find((cat) =>
        cat.id.isEqualTo(category.id)
      );
      if (removedCategory) {
        const specChange = new ProposalSpecChange(
          ProposalField.FeeScheduleCategory(category.id),
          ProposalChangeAction.Remove,
          category.toString()
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }

    return newBuilder;
  }

  public setDiscount(discount: Percent): ProposalBuilder {
    if (Masked.isMasked(this.currentSpec.discount)) {
      throw new Error("Cannot compare discount changes when new discount is masked");
    }
    if (
      (!this.currentSpec.discount && !discount) ||
      this.currentSpec.discount.isEqualTo(discount) ||
      Masked.isMasked(this._specStack[0].discount)
    ) {
      return this;
    }

    const newBuilder = this.clone();
    if (Masked.isMasked(newBuilder._specStack[0].discount)) {
      throw new Error("Cannot compare discount changes when original discount is masked");
    }

    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.discount = discount ?? 0;
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.Discount)
    );

    if (
      (!newBuilder._specStack[0].discount && !discount) ||
      newBuilder._specStack[0].discount?.isEqualTo(discount)
    ) {
      return newBuilder;
    }
    let action: ProposalChangeAction = ProposalChangeAction.Edit;
    let value = newBuilder._specStack[0].discount?.toString() ?? "";
    if (!newBuilder._specStack[0].discount.value && discount) {
      action = ProposalChangeAction.Add;
      value = discount.toString();
    } else if (newBuilder._specStack[0].discount.value && !discount.value) {
      action = ProposalChangeAction.Remove;
    }
    const specChange = new ProposalSpecChange(
      ProposalField.Discount,
      action,
      value
    );
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setConflictsDocuments(documents: WorkDocument[]): ProposalBuilder {
    const newBuilder = this.clone();

    if (Masked.isMasked(newBuilder.currentSpec?.conflictsCheckWaived) ||
      Masked.isMasked(newBuilder.currentSpec?.conflictsDocuments)) {
      throw new Error("Cannot set conflicts documents when current conflicts documents or conflicts check waiver is masked");
    }

    if (Masked.isMasked(newBuilder._specStack[0]?.conflictsCheckWaived) ||
      Masked.isMasked(newBuilder._specStack[0]?.conflictsDocuments)) {
      return this;
    }

    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.conflictsDocuments = _.uniqBy(
      documents,
      (document) => document.id.value
    );
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => change.field?.category !== ProposalFieldCategory.Conflicts
    );
    for (const document of documents) {
      const existingDocument = newBuilder._specStack[0].conflictsDocuments.find(
        (doc) => doc.id.isEqualTo(document.id)
      );
      if (!existingDocument) {
        const specChange = new ProposalSpecChange(
          ProposalField.ConflictsDocument(document.id),
          ProposalChangeAction.Add,
          document.name?.value ?? ""
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }
    for (const document of newBuilder._specStack[0]?.conflictsDocuments ?? []) {
      const removedDocument = !documents.find((doc) =>
        doc.id.isEqualTo(document.id)
      );
      if (removedDocument) {
        const specChange = new ProposalSpecChange(
          ProposalField.ConflictsDocument(document.id),
          ProposalChangeAction.Remove,
          document.name?.value ?? ""
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }

    if (
      newBuilder._specStack[0]?.conflictsCheckWaived?.value !==
      newBuilder.currentSpec?.conflictsCheckWaived?.value
    ) {
      const specChange = new ProposalSpecChange(
        ProposalField.WaiveConflictsCheck,
        newBuilder.currentSpec?.conflictsCheckWaived?.value
          ? ProposalChangeAction.Add
          : ProposalChangeAction.Remove,
        "Conflicts Check Waiver"
      );
      newBuilder._sessionHistory.push(specChange);
    }
    return newBuilder;
  }

  public setClientPolicyDocuments(documents: WorkDocument[]): ProposalBuilder {
    const newBuilder = this.clone();
    if (Masked.isMasked(newBuilder.currentSpec?.clientPolicyDocuments) ||
      Masked.isMasked(newBuilder._specStack[0]?.clientPolicyDocuments)) {
      throw new Error("Cannot set client policy documents when current client policy documents is masked");
    }

    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.clientPolicyDocuments = _.uniqBy(
      documents,
      (document) => document.id.value
    );
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => change.field?.name !== ProposalFieldName.ClientPolicies
    );
    for (const document of documents) {
      const existingDocument =
        newBuilder._specStack[0].clientPolicyDocuments.find((doc) =>
          doc.id.isEqualTo(document.id)
        );
      if (!existingDocument) {
        const specChange = new ProposalSpecChange(
          ProposalField.ClientPolicyDocument(document.id),
          ProposalChangeAction.Add,
          document.name?.value ?? ""
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }
    for (const document of newBuilder._specStack[0].clientPolicyDocuments ??
      []) {
      const removedDocument = !documents.find((doc) =>
        doc.id.isEqualTo(document.id)
      );
      if (removedDocument) {
        const specChange = new ProposalSpecChange(
          ProposalField.ClientPolicyDocument(document.id),
          ProposalChangeAction.Remove,
          document.name?.value ?? ""
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }

    return newBuilder;
  }

  public setVendorPolicyDocuments(documents: WorkDocument[]): ProposalBuilder {
    const newBuilder = this.clone();
    if (Masked.isMasked(newBuilder.currentSpec?.vendorPolicyDocuments) ||
      Masked.isMasked(newBuilder._specStack[0]?.vendorPolicyDocuments)) {
      throw new Error("Cannot set vendor policy documents when current vendor policy documents is masked");
    }

    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.vendorPolicyDocuments = _.uniqBy(
      documents,
      (document) => document.id.value
    );
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => change.field?.name !== ProposalFieldName.VendorPolicies
    );
    for (const document of documents) {
      const existingDocument =
        newBuilder._specStack[0]?.vendorPolicyDocuments.find((doc) =>
          doc.id.isEqualTo(document.id)
        );
      if (!existingDocument) {
        const specChange = new ProposalSpecChange(
          ProposalField.VendorPolicyDocument(document.id),
          ProposalChangeAction.Add,
          document.name?.value ?? ""
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }
    for (const document of newBuilder._specStack[0]?.vendorPolicyDocuments ??
      []) {
      const removedDocument = !documents.find((doc) =>
        doc.id.isEqualTo(document.id)
      );
      if (removedDocument) {
        const specChange = new ProposalSpecChange(
          ProposalField.VendorPolicyDocument(document.id),
          ProposalChangeAction.Remove,
          document.name?.value ?? ""
        );
        newBuilder._sessionHistory.push(specChange);
      }
    }
    return newBuilder;
  }

  public setClientTeamTemplateIds(templateIds: Guid[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.clientTeamTemplateIds = _.uniqBy(
      templateIds,
      (id) => id.value
    );
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public setVendorTeamTemplateIds(templateIds: Guid[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.vendorTeamTemplateIds = _.uniqBy(
      templateIds,
      (id) => id.value
    );
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public setClientFeeScheduleTemplateIds(templateIds: Guid[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.clientFeeScheduleTemplateIds = _.uniqBy(
      templateIds,
      (id) => id.value
    );
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public setVendorFeeScheduleTemplateIds(templateIds: Guid[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.vendorFeeScheduleTemplateIds = _.uniqBy(
      templateIds,
      (id) => id.value
    );
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public setConflictsCheckWaived(waived: AHBoolean): ProposalBuilder {
    if (
      Masked.isMasked(this._specStack[0]?.conflictsCheckWaived) ||
      Masked.isMasked(this.currentSpec?.conflictsCheckWaived) ||
      this.currentSpec?.conflictsCheckWaived?.value === waived?.value) {
      return this;
    }
    const newBuilder = this.clone();
    if (Masked.isMasked(newBuilder._specStack[0]?.conflictsCheckWaived))
      throw new Error("Cannot compare conflicts check waived changes when original is masked");
    if (Masked.isMasked(newBuilder.currentSpec?.conflictsCheckWaived))
      throw new Error("Cannot compare conflicts check waived changes when current is masked");
    if (Masked.isMasked(newBuilder._specStack[0]?.conflictsDocuments))
      throw new Error("Cannot compare conflicts check waived changes when original conflicts documents are masked");
    if (Masked.isMasked(newBuilder.currentSpec?.conflictsDocuments))
      throw new Error("Cannot compare conflicts check waived changes when current conflicts documents are masked");
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.conflictsCheckWaived = waived;
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.WaiveConflictsCheck)
    );
    if (
      newBuilder._specStack[0]?.conflictsCheckWaived?.value === waived?.value
    ) {
      return newBuilder;
    }
    const specChange = new ProposalSpecChange(
      ProposalField.WaiveConflictsCheck,
      waived.value ? ProposalChangeAction.Add : ProposalChangeAction.Remove,
      "Conflicts Check Waiver"
    );
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setTeamRestricted(teamRestricted: AHBoolean): ProposalBuilder {
    if (
      Masked.isMasked(this._specStack[0]?.teamRestricted) ||
      Masked.isMasked(this.currentSpec?.teamRestricted) ||
      this.currentSpec?.teamRestricted?.value === teamRestricted?.value) {
      return this;
    }

    const newBuilder = this.clone();
    if (Masked.isMasked(newBuilder._specStack[0]?.teamRestricted)) {
      throw new Error("Cannot compare team restriction changes when original is masked");
    }
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.teamRestricted = new AHBoolean(teamRestricted.value);
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;

    newBuilder._sessionHistory = newBuilder._sessionHistory.filter(
      (change) => !change.field?.isEqualTo(ProposalField.TeamRestriction)
    );
    if (
      newBuilder._specStack[0]?.teamRestricted?.value === teamRestricted?.value
    ) {
      return newBuilder;
    }
    const specChange = new ProposalSpecChange(
      ProposalField.TeamRestriction,
      teamRestricted.value
        ? ProposalChangeAction.Add
        : ProposalChangeAction.Remove,
      "Team Restriction"
    );
    newBuilder._sessionHistory.push(specChange);

    return newBuilder;
  }

  public setClientReviewers(newReviewers: ProposalReviewer[]): ProposalBuilder {
    if (
      this.currentSpec.clientReviewers?.length === newReviewers.length &&
      this.currentSpec.clientReviewers.every((currentReviewer) =>
        newReviewers.some(r => r.isEqualTo(currentReviewer))
      )
    ) {
      return this;
    }
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.clientReviewers = newReviewers;
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public setVendorReviewers(reviewers: ProposalReviewer[]): ProposalBuilder {
    const newBuilder = this.clone();
    newBuilder.updateCurrentIndex();
    const newSpec = newBuilder.currentSpec.clone();
    newSpec.vendorReviewers = reviewers;
    newBuilder._specStack.push(newSpec);
    newBuilder._currentIndex = newBuilder._specStack.length - 1;
    return newBuilder;
  }

  public buildNewDraft(session: Readonly<Session>): Proposal {
    return ProposalFactory.createProposal(
      this.completedSpec,
      this._currentUser,
      session
    );
  }

  public updateDraftProposal(
    oldProposal: Proposal,
    session: Readonly<Session>
  ): Proposal {
    return ProposalFactory.updateProposal(
      oldProposal,
      this._currentUser,
      this.completedSpec,
      session
    );
  }

  public toJSON(): ProposalBuilderJSON {
    return {
      currentUser: this._currentUser.toJSON(),
      specStack: this._specStack.map((spec) => spec.toJSON()),
      currentIndex: this._currentIndex,
      sessionHistory: this._sessionHistory.map((change) => change.toJSON()),
      lastUpdated: this._lastUpdated.toJSON()
    };
  }

  public static fromJSON(json: ProposalBuilderJSON): ProposalBuilder {
    if (!json) {
      throw new Error("Failed to parse ProposalBuilder from JSON");
    }
    const specStack = json.specStack.map((spec) => ProposalSpec.fromJSON(spec));
    if (!specStack) {
      throw new Error("Failed to parse ProposalBuilder from JSON");
    }
    const sessionHistory = json.sessionHistory.map((change) =>
      ProposalSpecChange.fromJSON(change)
    ).filter((change): change is ProposalSpecChange => !!change);
    const currentUser = User.fromJSON(json.currentUser);
    const builder = new ProposalBuilder(currentUser);
    builder._specStack = specStack;
    builder._currentIndex = json.currentIndex;
    builder._sessionHistory = sessionHistory;
    builder._lastUpdated = Date.fromJSON(json.lastUpdated)
      ?? new Date(moment().subtract(1000, "years"));
    return builder;
  }

  public static fromWorkAgreement(
    user: User,
    workAgreement: WorkAgreement
  ): ProposalBuilder {
    const spec: CompleteProposalSpec = {
      name: workAgreement.name?.clone(),
      client: workAgreement.client?.clone(),
      vendors: workAgreement.vendors.map((vendor) => vendor.clone()),
      description: workAgreement.description?.clone(),
      negotiable: true,
      vendorFeeScheduleTemplateIds: workAgreement.vendorFeeScheduleTemplateIds ?? [],
      clientFeeScheduleTemplateIds: workAgreement.clientFeeScheduleTemplateIds ?? [],
      vendorTeamTemplateIds: workAgreement.vendorTeamTemplateIds ?? [],
      clientTeamTemplateIds: workAgreement.clientTeamTemplateIds ?? [],
      vendorPolicyDocuments: workAgreement.vendorPolicyDocuments ?? [],
      clientPolicyDocuments: workAgreement.clientPolicyDocuments ?? [],
      conflictsDocuments: workAgreement.conflictsDocuments ?? [],
      conflictsCheckWaived: workAgreement.conflictsCheckWaived ?? new AHBoolean(false),
      teamRestricted: workAgreement.teamRestricted ?? new AHBoolean(false),
      vendorTeam: Masked.isMasked(workAgreement.team) ? workAgreement.team :
        workAgreement.team?.clone() as DetailedVendorTeam,
      feeSchedule: workAgreement.feeSchedule ?? [],
      discount:
        Masked.isMasked(workAgreement.discount) ? workAgreement.discount :
          workAgreement.discount ?? new Percent(3),
    };
    return new ProposalBuilder(user, spec, workAgreement.hireDate);
  }

  public clone(): ProposalBuilder {
    const clone = new ProposalBuilder(this._currentUser);
    clone._specStack = this._specStack.map((spec) => spec.clone());
    clone._sessionHistory = this._sessionHistory.map((change) =>
      change.clone()
    );
    clone._currentIndex = this._currentIndex;
    clone._lastSavedIndex = this._lastSavedIndex;
    return clone;
  }

  public mergeUpdatedSpec(updatedSpec: CompleteProposalSpec): ProposalBuilder {
    let newBuilder = new ProposalBuilder(
      this._currentUser,
      updatedSpec
    );

    const originalName = this._specStack[0].name;
    const nameUnchanged =
      (!updatedSpec.name && !originalName) ||
      updatedSpec.name?.isEqualTo(originalName);
    if (nameUnchanged && this.currentSpec.name) {
      newBuilder = newBuilder.setName(this.currentSpec.name);
    }

    const originalDescription = this._specStack[0].description;
    const descriptionUnchanged =
      (!updatedSpec.description && !originalDescription) ||
      updatedSpec.description?.isEqualTo(originalDescription);
    if (descriptionUnchanged && this.currentSpec.description) {
      newBuilder = newBuilder.setDescription(this.currentSpec.description);
    }

    const originalNegotiable = this._specStack[0].negotiable;
    const negotiableUnchanged =
      (!updatedSpec.negotiable && !originalNegotiable) ||
      updatedSpec.negotiable === originalNegotiable;
    if (negotiableUnchanged && this.currentSpec.negotiable !== undefined) {
      newBuilder = newBuilder.setNegotiable(this.currentSpec.negotiable);
    }

    const originalResponseDueBy = this._specStack[0].responseDueBy;
    const responseDueByUnchanged =
      (!updatedSpec.responseDueBy && !originalResponseDueBy) ||
      updatedSpec.responseDueBy?.isEqualTo(originalResponseDueBy);
    if (responseDueByUnchanged) {
      newBuilder = newBuilder.setResponseDueBy(this.currentSpec.responseDueBy);
    }

    const originalStartDate = this._specStack[0].startDate;
    const startDateUnchanged =
      (!updatedSpec.startDate && !originalStartDate) ||
      updatedSpec.startDate?.isEqualTo(originalStartDate);
    if (startDateUnchanged) {
      newBuilder = newBuilder.setStartDate(this.currentSpec.startDate);
    }

    const originalEndDate = this._specStack[0].endDate;
    const endDateUnchanged =
      (!updatedSpec.endDate && !originalEndDate) ||
      updatedSpec.endDate?.isEqualTo(originalEndDate);
    if (endDateUnchanged) {
      newBuilder = newBuilder.setEndDate(this.currentSpec.endDate);
    }

    const originalTeam = this._specStack[0].team;
    const teamUnchanged =
      Masked.isMasked(updatedSpec.vendorTeam) ||
      (!updatedSpec.vendorTeam && !originalTeam) ||
      updatedSpec.vendorTeam?.isEqualTo(originalTeam);
    if (teamUnchanged && this.currentSpec.team instanceof DetailedVendorTeam) {
      newBuilder = newBuilder.setTeam(this.currentSpec.team);
    } else if (
      this.currentSpec.team instanceof DetailedVendorTeam &&
      originalTeam instanceof DetailedVendorTeam &&
      updatedSpec.vendorTeam instanceof DetailedVendorTeam
    ) {
      newBuilder = newBuilder.setTeam(
        ProposalBuilder.mergeTeams(
          originalTeam,
          this.currentSpec.team,
          updatedSpec.vendorTeam
        )
      );
    }

    const originalFeeSchedule = this._specStack[0].feeSchedule;
    const feeScheduleUnchanged =
      Masked.isMasked(updatedSpec.feeSchedule) ||
      Masked.isMasked(originalFeeSchedule) ||
      (!updatedSpec.feeSchedule && !originalFeeSchedule) ||
      (updatedSpec.feeSchedule?.every((cat) =>
        originalFeeSchedule?.some((originalCat) => originalCat.isEqualTo(cat))
      ) &&
        originalFeeSchedule?.every((cat) =>
          Masked.isNotMasked(updatedSpec.feeSchedule) && // ts can't track checks outside nested function
          updatedSpec.feeSchedule?.some((newCat) => newCat.isEqualTo(cat))
        ));

    if (feeScheduleUnchanged && Masked.isNotMasked(this.currentSpec.feeSchedule)) {
      newBuilder = newBuilder.setFeeSchedule(this.currentSpec.feeSchedule);
    } else if (
      Masked.isNotMasked(this.currentSpec.feeSchedule) &&
      Masked.isNotMasked(originalFeeSchedule) &&
      Masked.isNotMasked(updatedSpec.feeSchedule)
    ) {
      newBuilder = newBuilder.setFeeSchedule(
        ProposalBuilder.mergeFeeSchedules(
          originalFeeSchedule,
          this.currentSpec.feeSchedule,
          updatedSpec.feeSchedule
        )
      );
    }

    const originalDiscount = this._specStack[0].discount;
    const discountUnchanged = Masked.isMasked(updatedSpec.discount) ||
      (!updatedSpec.discount && !originalDiscount) ||
      (Masked.isNotMasked(originalDiscount) && updatedSpec.discount?.isEqualTo(originalDiscount));
    if (discountUnchanged && Masked.isNotMasked(this.currentSpec.discount)) {
      newBuilder = newBuilder.setDiscount(this.currentSpec.discount);
    }

    const originalConflictsDocuments = this._specStack[0].conflictsDocuments;
    if (Masked.isNotMasked(originalConflictsDocuments) &&
      Masked.isNotMasked(updatedSpec.conflictsDocuments) &&
      Masked.isNotMasked(this.currentSpec.conflictsDocuments)) {

      const conflictsDocumentsUnchanged =
        (!updatedSpec.conflictsDocuments && !originalConflictsDocuments) ||
        (updatedSpec.conflictsDocuments?.every((doc) =>
          originalConflictsDocuments?.some((originalDoc) =>
            originalDoc.isEqualTo(doc)
          )
        ) &&
          originalConflictsDocuments?.every((doc) =>
            Masked.isNotMasked(updatedSpec.conflictsDocuments) && // ts can't track checks outside nested function
            updatedSpec.conflictsDocuments?.some((newDoc) =>
              newDoc.isEqualTo(doc)
            )
          ));
      if (conflictsDocumentsUnchanged) {
        newBuilder = newBuilder.setConflictsDocuments(
          this.currentSpec.conflictsDocuments
        );
      } else {
        newBuilder = newBuilder.setConflictsDocuments(
          ProposalBuilder.mergeDocuments(
            originalConflictsDocuments,
            this.currentSpec.conflictsDocuments,
            updatedSpec.conflictsDocuments
          )
        );
      }
    }

    const originalClientPolicyDocuments =
      this._specStack[0].clientPolicyDocuments;

    if (Masked.isNotMasked(originalClientPolicyDocuments) &&
      Masked.isNotMasked(updatedSpec.clientPolicyDocuments) &&
      Masked.isNotMasked(this.currentSpec.clientPolicyDocuments)) {
      const clientPolicyDocumentsUnchanged =
        (!updatedSpec.clientPolicyDocuments && !originalClientPolicyDocuments) ||
        (updatedSpec.clientPolicyDocuments?.every((doc) =>
          originalClientPolicyDocuments?.some((originalDoc) =>
            originalDoc.isEqualTo(doc)
          )
        ) &&
          originalClientPolicyDocuments?.every((doc) =>
            Masked.isNotMasked(updatedSpec.clientPolicyDocuments) && // ts can't track checks outside nested function
            updatedSpec.clientPolicyDocuments?.some((newDoc) =>
              newDoc.isEqualTo(doc)
            )
          ));
      if (clientPolicyDocumentsUnchanged) {
        newBuilder = newBuilder.setClientPolicyDocuments(
          this.currentSpec.clientPolicyDocuments
        );
      } else {
        newBuilder = newBuilder.setClientPolicyDocuments(
          ProposalBuilder.mergeDocuments(
            originalClientPolicyDocuments,
            this.currentSpec.clientPolicyDocuments,
            updatedSpec.clientPolicyDocuments
          )
        );
      }
    }

    const originalVendorPolicyDocuments =
      this._specStack[0].vendorPolicyDocuments;

    if (Masked.isNotMasked(originalVendorPolicyDocuments) &&
      Masked.isNotMasked(updatedSpec.vendorPolicyDocuments) &&
      Masked.isNotMasked(this.currentSpec.vendorPolicyDocuments)) {
      const vendorPolicyDocumentsUnchanged =
        (!updatedSpec.vendorPolicyDocuments && !originalVendorPolicyDocuments) ||
        (updatedSpec.vendorPolicyDocuments?.every((doc) =>
          originalVendorPolicyDocuments?.some((originalDoc) =>
            originalDoc.isEqualTo(doc)
          )
        ) &&
          originalVendorPolicyDocuments?.every((doc) =>
            Masked.isNotMasked(updatedSpec.vendorPolicyDocuments) && // ts can't track checks outside nested function
            updatedSpec.vendorPolicyDocuments?.some((newDoc) =>
              newDoc.isEqualTo(doc)
            )
          ));
      if (vendorPolicyDocumentsUnchanged) {
        newBuilder = newBuilder.setVendorPolicyDocuments(
          ProposalBuilder.mergeDocuments(
            originalVendorPolicyDocuments,
            this.currentSpec.vendorPolicyDocuments,
            updatedSpec.vendorPolicyDocuments
          )
        );
      }
    }

    const originalClientReviewers = this._specStack[0].clientReviewers;
    const clientReviewersUnchanged =
      (!updatedSpec.clientReviewers && !originalClientReviewers) ||
      (updatedSpec.clientReviewers?.every((reviewer) =>
        originalClientReviewers?.some((originalReviewer) =>
          originalReviewer.isEqualTo(reviewer)
        )
      ) &&
        originalClientReviewers?.every((reviewer) =>
          updatedSpec.clientReviewers?.some((newReviewer) =>
            newReviewer.isEqualTo(reviewer)
          )
        ));
    if (clientReviewersUnchanged && this.currentSpec.clientReviewers) {
      newBuilder = newBuilder.setClientReviewers(
        this.currentSpec.clientReviewers
      );
    }

    const originalVendorReviewers = this._specStack[0].vendorReviewers;
    const vendorReviewersUnchanged =
      (!updatedSpec.vendorReviewers && !originalVendorReviewers) ||
      (updatedSpec.vendorReviewers?.every((reviewer) =>
        originalVendorReviewers?.some((originalReviewer) =>
          originalReviewer.isEqualTo(reviewer)
        )
      ) &&
        originalVendorReviewers?.every((reviewer) =>
          updatedSpec.vendorReviewers?.some((newReviewer) =>
            newReviewer.isEqualTo(reviewer)
          )
        ));
    if (vendorReviewersUnchanged && this.currentSpec.vendorReviewers) {
      newBuilder = newBuilder.setVendorReviewers(
        this.currentSpec.vendorReviewers
      );
    }

    if (!this.isModified) {
      newBuilder.setLastSavedIndex();
    }
    return newBuilder;
  }

  private static mergeDocuments(
    originalDocuments: WorkDocument[],
    currentDocuments: WorkDocument[],
    newDocuments: WorkDocument[]
  ): WorkDocument[] {
    const removedDocuments: WorkDocument[] = [];
    for (const originalDocument of originalDocuments ?? []) {
      const documentRemoved =
        !newDocuments.find((newDocument) =>
          newDocument.isEqualTo(originalDocument)
        ) ||
        !currentDocuments.find((currentDocument) =>
          currentDocument.isEqualTo(originalDocument)
        );
      if (documentRemoved) {
        removedDocuments.push(originalDocument);
      }
    }

    const mergedDocuments = newDocuments.filter(
      (updatedDocument) =>
        !removedDocuments.some((removedDocument) =>
          removedDocument.isEqualTo(updatedDocument)
        )
    );
    for (const document of currentDocuments ?? []) {
      if (
        !mergedDocuments?.find((newDocument) =>
          newDocument.isEqualTo(document)
        ) &&
        !removedDocuments.find((removedDocument) =>
          removedDocument.isEqualTo(document)
        )
      ) {
        mergedDocuments?.push(document);
      }
    }
    return mergedDocuments;
  }

  private static mergeTeams(
    originalTeam?: DetailedVendorTeam,
    currentTeam?: DetailedVendorTeam,
    newTeam?: DetailedVendorTeam
  ): DetailedVendorTeam {
    let leader = newTeam?.leader;
    const leaderUnchanged =
      (!newTeam?.leader && !originalTeam?.leader) ||
      newTeam?.leader?.isEqualTo(originalTeam?.leader);
    if (leaderUnchanged) {
      leader = currentTeam?.leader;
    }

    const removedMembers: Individual[] = [];
    for (const originalMember of originalTeam?.members ?? []) {
      const memberRemoved =
        !newTeam?.members.find((newMember) =>
          newMember.isEqualTo(originalMember)
        ) ||
        !currentTeam?.members.find((currentMember) =>
          currentMember.isEqualTo(originalMember)
        );
      if (memberRemoved) {
        removedMembers.push(originalMember);
      }
    }

    const mergedMembers = newTeam?.members.filter(
      (newMember) =>
        !removedMembers.some((removedMember) =>
          removedMember.isEqualTo(newMember)
        )
    );
    for (const member of currentTeam?.members ?? []) {
      if (
        !mergedMembers?.find((newMember) => newMember.isEqualTo(member)) &&
        !removedMembers.find((removedMember) => removedMember.isEqualTo(member))
      ) {
        mergedMembers?.push(member);
      }
    }
    return new DetailedVendorTeam(
      leader,
      mergedMembers ?? []
    );
  }

  private static mergeFeeSchedules(
    originalFeeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[],
    currentFeeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[],
    newFeeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[]
  ): FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] {
    const removedCategories: FeeScheduleCategory[] = [];
    const removedCategoriesMasked: FeeScheduleCategoryMaskedCurrency[] = [];
    for (const originalCategory of originalFeeSchedule ?? []) {
      const categoryRemoved =
        !newFeeSchedule?.find((newCategory) =>
          newCategory.id.isEqualTo(originalCategory.id)
        ) ||
        !currentFeeSchedule?.find((currentCategory) =>
          currentCategory.id.isEqualTo(originalCategory.id)
        );

      const currentCategory = currentFeeSchedule?.find((currentCategory) =>
        currentCategory.id.isEqualTo(originalCategory.id)
      );
      const modifiedLocally =
        currentCategory && !currentCategory.isEqualTo(originalCategory);

      // noinspection SuspiciousTypeOfGuard
      if (categoryRemoved && !modifiedLocally && originalCategory instanceof FeeScheduleCategory) {
        removedCategories.push(originalCategory);
      } else if (categoryRemoved && !modifiedLocally) {
        removedCategoriesMasked.push(originalCategory);
      }
    }

    const mergedCategories = newFeeSchedule.filter(
      (updatedCategory) =>
        !removedCategories.some((removedCategory) =>
          removedCategory.id.isEqualTo(updatedCategory.id)
        ) &&
        !removedCategoriesMasked.some((removedCategory) =>
          removedCategory.id.isEqualTo(updatedCategory.id)
        )
    );
    for (const category of currentFeeSchedule ?? []) {
      if (
        !mergedCategories?.find((newCategory) =>
          newCategory.id.isEqualTo(category.id)
        ) &&
        !removedCategories.find((removedCategory) =>
          removedCategory.id.isEqualTo(category.id)
        ) &&
        !removedCategoriesMasked.find((removedCategory) =>
          removedCategory.id.isEqualTo(category.id)
        )
      ) {
        mergedCategories?.push(category);
      }
    }
    return mergedCategories;
  }

  private updateCurrentIndex() {
    if (this._currentIndex < this._specStack.length - 1) {
      this._specStack.push(this._specStack[this._currentIndex]);
    }
    this._currentIndex = this._specStack.length - 1;
  }

  private get completedSpec(): CompleteProposalSpec {
    return {
      name: this.currentSpec.name,
      client: this.currentSpec.client,
      vendors: this.currentSpec.vendors,
      description: this.currentSpec.description,
      negotiable: this.currentSpec.negotiable ?? true,
      responseDueBy: this.currentSpec.responseDueBy,
      startDate: this.currentSpec.startDate,
      endDate: this.currentSpec.endDate,
      clientReviewers: this.currentSpec.clientReviewers ?? [],
      vendorReviewers: this.currentSpec.vendorReviewers ?? [],
      vendorFeeScheduleTemplateIds:
        this.currentSpec.vendorFeeScheduleTemplateIds ?? [],
      clientFeeScheduleTemplateIds:
        this.currentSpec.clientFeeScheduleTemplateIds ?? [],
      vendorTeamTemplateIds: this.currentSpec.vendorTeamTemplateIds ?? [],
      clientTeamTemplateIds: this.currentSpec.clientTeamTemplateIds ?? [],
      vendorPolicyDocuments: this.currentSpec.vendorPolicyDocuments ?? [],
      clientPolicyDocuments: this.currentSpec.clientPolicyDocuments ?? [],
      conflictsDocuments: this.currentSpec.conflictsDocuments ?? [],
      conflictsCheckWaived:
        this.currentSpec.conflictsCheckWaived ?? new AHBoolean(false),
      teamRestricted: this.currentSpec.teamRestricted ?? new AHBoolean(false),
      vendorTeam: this.currentSpec.team,
      feeSchedule: this.currentSpec.feeSchedule ?? [],
      discount: this.currentSpec.discount ?? new Percent(0),
    };
  }
}
